/* eslint-disable dot-notation */
/* eslint-disable keyword-spacing */
import { Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';
import { Auth0Provider } from '@auth0/auth0-react';

import { CustomProvider } from 'rsuite';
import styled from 'styled-components';

import Header from 'src/components/header';
import NavigationProvider from 'src/routing/NavigationProvider';
import { toReactRouterPath } from 'src/routing/pathUtils';
import InformationPanel from 'src/components/information-panel';
import AuthHeader from 'src/routes/auth/components/auth-header';

import { userThemeSelector, userRoleSelector } from 'src/store/user/user.selectors';
import { publicRoutes, getDefaultRoute, NotFoundPage } from 'src/constants/routes.config';
import { accessiblePrivateRoutesSelector, defaultRouteForContextSelector } from 'src/config/navigation.selectors';
import AppMaintenanceGate from 'src/components/app/AppMaintenanceGate';
import AppPostLoginMaintenanceGate from 'src/components/app/AppPostLoginMaintenanceGate';
import AppNavigationGate from 'src/components/app/AppNavigationGate';
import AppAuthGate from 'src/components/app/AppAuthGate';
import ExperienceFallbackRoute from 'src/components/app/ExperienceFallbackRoute';
import AuthenticatedLoginRedirect from 'src/components/app/AuthenticatedLoginRedirect';
import UnauthenticatedFallbackRoute from 'src/components/app/UnauthenticatedFallbackRoute';
import { sessionAccessSelector, sessionAccessTokenSelector } from 'src/routes/auth/auth.selectors';
import { clearAuthMutualData, clearSession } from 'src/routes/auth/auth.actions';
import { bookList } from 'src/constants/bookList';
import { buildAuthLoginSearch, getAuthLoginRedirectUri } from 'src/routes/auth/auth.redirect';
import { initializeForExistingSession } from 'src/store/initialization/initialization.operations';
import { getUserPersonalInformation } from 'src/store/user/user.operations';
import { setUserPersonalInformation } from 'src/store/user/user.actions';
import jwtDecode from 'jwt-decode';
import { getParamURL } from 'src/utils/url';

import styles from 'src/components/styles.scss';
import { IntlProvider } from 'src/providers/IntlProvider';
import ThemeProvider from 'src/components/styled/theme/ThemeProvider';
import { ModalProvider } from 'src/components/ui-core/modal';
import LytSidebar from 'src/components/sidebar/LytSidebar';
import ToastManager from 'src/components/toast/ToastManager';
import modalRegistry from 'src/components/modals/modal-registry';
import { env } from 'src/config/env';
import { AuthBody, AuthWrapper } from 'src/components/auth/AuthLayout.styled';
import { isProduction } from 'src/utils/environment';
// import ToastDemo from 'src/components/toast/ToastDemo'; // TEMPORARY - Uncomment for QA testing
// Removed route-based scopes - now using agency-based AbortController

const AuthRouteRedirect = ({ to }) => {
    const { pathname, search } = useLocation();

    return (
        <Navigate
            to={{ pathname: to, search: buildAuthLoginSearch(pathname, search) }}
            replace
        />
    );
};

AuthRouteRedirect.propTypes = {
    to: PropTypes.string.isRequired,
};

const AppWrapper = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    position: relative;
`;

const AppBody = styled.div`
    flex: 1;
    min-width: 0;
    padding: 40px;
    height: 100vh;
    overflow-y: auto;
    transform: translateZ(0);
    backface-visibility: hidden;
    will-change: transform;
`;

const PortalContainer = styled.div`
    position: fixed;
    left: -200px;  /* Position off-screen by default */
    top: 0;
    z-index: ${({ $zIndex }) => $zIndex || 9999};
    pointer-events: none;
    width: 0;
    height: 0;

    & > * {
        pointer-events: auto;
    }
`;

// Eager scope bootstrap to eliminate first-render races
const App = () => {
    const dispatch = useDispatch();
    const access = useSelector((state) => sessionAccessSelector(state));
    const accessToken = useSelector(sessionAccessTokenSelector);
    const theme = useSelector(userThemeSelector);
    const userRole = useSelector(userRoleSelector);
    const accessibleRoutes = useSelector(accessiblePrivateRoutesSelector);
    const contextDefaultRoute = useSelector(defaultRouteForContextSelector);
    // eslint-disable-next-line no-unused-vars
    const simplifiedTheme = theme.replace('theme-', '');
    const scrollToTopButton = useRef(null);

    // Initialize GA only if key is provided.
    // Non-production environments send events with GA4's debug_mode flag so
    // they surface in DebugView / Realtime but are excluded from standard
    // reports, audiences, conversions, and BigQuery exports.
    if (env.googleAnalyticsKey) {
        ReactGA.initialize(env.googleAnalyticsKey, {
            gtagOptions: { debug_mode: !isProduction }
        });
    }

    useLayoutEffect(() => {
        if (getParamURL('idUser') && getParamURL('token') && access) {
            console.log('app - clearSession');
            dispatch(clearSession());
        }
        if (getParamURL('invitation') && access) {
            console.log('app - clearSession');
            dispatch(clearSession());
        }
        window.onscroll = () => scrollFunction();

        scrollFunction();

        return () => dispatch(clearAuthMutualData());
    }, []);

    useEffect(() => {
        const html = document.querySelector('html');
        html.dataset.theme = theme;
    }, [theme]);

    // Initialize transient data (roles, agencies) for already-authenticated
    // sessions. With redux-persist removed, redux state always starts empty
    // on a hard reload, so the `access && userRole` branch is effectively a
    // defensive no-op today (kept in case future work reintroduces a hydrated
    // identity path). The `access && !userRole && accessToken` branch fires
    // when Auth0 silent re-auth populates the token before user info is
    // fetched — we decode the JWT to seed the email and trigger the same
    // bootstrap chain a fresh login uses.
    useEffect(() => {
        if (access && userRole) {
            // usertype already in state — initialize normally
            dispatch(initializeForExistingSession()).catch((error) => {
                console.warn('Failed to initialize application for existing session:', error);
            });
        } else if (access && !userRole && accessToken) {
            // access + token present but usertype is empty (e.g. Auth0 silent
            // re-auth). Decode the JWT to get the user's email, seed it into
            // state, then call getUserPersonalInformation (which reads email
            // from state). Its .then() calls initializeAfterLogin() to load
            // agencies/roles.
            try {
                const decoded = jwtDecode(accessToken);
                const email = decoded?.userProfile?.email;
                if (email) {
                    dispatch(setUserPersonalInformation({ email }));
                    dispatch(getUserPersonalInformation());
                }
            } catch (e) {
                console.error('Failed to decode access token for user bootstrap:', e);
            }
        }
    }, [access, userRole, accessToken, dispatch]);

    const scrollFunction = () => {
        if (scrollToTopButton?.current) {
            if (
                document.body.scrollTop > 20
                || document.documentElement.scrollTop > 20
            ) {
                scrollToTopButton.current.style.display = 'flex';
            } else {
                scrollToTopButton.current.style.display = 'none';
            }
        }
    };

    return (
        <CustomProvider theme={simplifiedTheme}>
            <ThemeProvider theme={simplifiedTheme}>
                <IntlProvider>
                    <AppMaintenanceGate>
                    <Auth0Provider
                        domain={env.auth0Domain}
                        clientId={env.auth0ClientId}
                        // Cypress/automation: persist tokens so cy.session can restore them.
                        // Normal users keep the default in-memory cache.
                        cacheLocation={
                            typeof window !== 'undefined'
                            && (window.Cypress || navigator.webdriver)
                                ? 'localstorage'
                                : 'memory'
                        }
                        authorizationParams={{
                            audience: env.auth0Audience,
                            scope: 'openid profile email read:user',
                            redirect_uri: getAuthLoginRedirectUri(),
                        }}
                    >
                        <BrowserRouter>
                        <NavigationProvider />
                        <ToastContainer
                            position="top-right"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                        />
                        <AppAuthGate>
                        {!access
                            ? <AuthWrapper>
                                <InformationPanel />
                                <AuthBody>
                                    <AuthHeader />
                                    <Suspense fallback={<div>loading...</div>}>
                                        <Routes>
                                            {publicRoutes.map(({ component: Component, path }) =>
                                                <Route
                                                    key={path}
                                                    path={toReactRouterPath(path)}
                                                    element={<Component />}
                                                />
                                            )}
                                            <Route path="/" element={<AuthRouteRedirect to={bookList.auth.login} />} />
                                            <Route path="*" element={<UnauthenticatedFallbackRoute />} />
                                        </Routes>
                                    </Suspense>
                                </AuthBody>
                            </AuthWrapper>
                            : <AppNavigationGate>
                                <AppPostLoginMaintenanceGate>
                                <AppWrapper className={styles['app__wrapper']} id="app">
                                    <LytSidebar />
                                    <AppBody 
                                        className={styles['app__body']}
                                        $currentPath={window.location.pathname}
                                    >
                                        <Header />
                                        <ModalProvider registry={modalRegistry}>
                                        <Suspense fallback={<div>loading...</div>}>
                                            <Routes>
                                                {accessibleRoutes.map(({ component: Component, path }) =>
                                                    <Route
                                                        key={path}
                                                        path={toReactRouterPath(path)}
                                                        element={<Component />}
                                                    />
                                                )}
                                                <Route path={bookList.notFound} element={<NotFoundPage />} />
                                                <Route path={toReactRouterPath(bookList.auth.login)} element={<AuthenticatedLoginRedirect />} />
                                                <Route path="/" element={<Navigate to={contextDefaultRoute || getDefaultRoute()} replace />} />
                                                <Route path="*" element={<ExperienceFallbackRoute />} />
                                            </Routes>
                                        </Suspense>
                                        </ModalProvider>
                                    </AppBody>
                                    <PortalContainer id="tooltip-root" $zIndex={10000} />
                                    <PortalContainer id="modal-root" $zIndex={9000} />
                                    <PortalContainer id="dropdown-root" $zIndex={9500} />
                                    <PortalContainer id="toast-root" $zIndex={11000} />
                                    <ToastManager />
                                    {/* <ToastDemo /> */} {/* TEMPORARY - Uncomment for QA testing */}
                                </AppWrapper>
                                </AppPostLoginMaintenanceGate>
                            </AppNavigationGate>
                        }
                        </AppAuthGate>
                        </BrowserRouter>
                    </Auth0Provider>
                    </AppMaintenanceGate>
                </IntlProvider>
            </ThemeProvider>
        </CustomProvider>
    );
};

export default App;
